import type { MenuItem } from '../store/useStore'

/**
 * #2028 メニューを商品名で探す (日本語・ローマ字)。
 *
 * 照らし合わせるのは **商品名** と **読み** (`nameReading`、サーバが商品名から作って配る。
 * 保存はしていない — services/pay/api/services/menu_reading.py)。説明文は含めない
 * (説明に「天ぷら」とある別の品まで出てしまうため)。英語名・各言語の名前は #2030。
 *
 * - かな・漢字の入力: 商品名と読みに部分一致。ひらがな/カタカナ・全角/半角・大文字/小文字は揃える
 *   (「天ぷら」「てんぷら」「テンプラ」「ﾃﾝﾌﾟﾗ」「てん」)
 * - ローマ字の入力: ひらがなに直して読みに部分一致 (「tenpura」「tempura」「karaage」)。
 *   ローマ字は長音の書き方が人によって違う (unaju / unajuu、shochu / shouchuu) ので、
 *   **ローマ字のときだけ** 読みと入力の両方で長音 (ー、お段・う段の後の う・お) を落として比べる。
 *   かなの入力で落とすと「とう」が「と」になり、関係ない品まで出る
 * - 英単語 (corn, beer) はローマ字にならないので、ここでは探さない (#2030)
 */

/** 比べる前に揃える: 全角/半角・大文字/小文字・カタカナ→ひらがな・空白と記号を除く。 */
export function normalizeForSearch(s: string): string {
  return toHiragana(s.normalize('NFKC').toLowerCase())
    // 長音 (ー) は残す (かなの入力ではそのまま比べる)。それ以外の記号・空白は落とす
    .replace(/[^\p{L}\p{N}ー]/gu, '')
}

function toHiragana(s: string): string {
  return s.replace(/[ァ-ヶ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60))
}

// ---- ローマ字 → ひらがな ---------------------------------------------------

const BASE: Record<string, string> = {
  a: 'あ', i: 'い', u: 'う', e: 'え', o: 'お',
  ka: 'か', ki: 'き', ku: 'く', ke: 'け', ko: 'こ',
  sa: 'さ', si: 'し', shi: 'し', su: 'す', se: 'せ', so: 'そ',
  ta: 'た', ti: 'ち', chi: 'ち', tu: 'つ', tsu: 'つ', te: 'て', to: 'と',
  na: 'な', ni: 'に', nu: 'ぬ', ne: 'ね', no: 'の',
  ha: 'は', hi: 'ひ', hu: 'ふ', fu: 'ふ', he: 'へ', ho: 'ほ',
  ma: 'ま', mi: 'み', mu: 'む', me: 'め', mo: 'も',
  ya: 'や', yu: 'ゆ', yo: 'よ',
  ra: 'ら', ri: 'り', ru: 'る', re: 'れ', ro: 'ろ',
  wa: 'わ', wi: 'うぃ', we: 'うぇ', wo: 'を',
  ga: 'が', gi: 'ぎ', gu: 'ぐ', ge: 'げ', go: 'ご',
  za: 'ざ', zi: 'じ', ji: 'じ', zu: 'ず', ze: 'ぜ', zo: 'ぞ',
  da: 'だ', di: 'ぢ', du: 'づ', de: 'で', do: 'ど',
  ba: 'ば', bi: 'び', bu: 'ぶ', be: 'べ', bo: 'ぼ',
  pa: 'ぱ', pi: 'ぴ', pu: 'ぷ', pe: 'ぺ', po: 'ぽ',
  fa: 'ふぁ', fi: 'ふぃ', fe: 'ふぇ', fo: 'ふぉ',
  ja: 'じゃ', ju: 'じゅ', je: 'じぇ', jo: 'じょ',
  sha: 'しゃ', shu: 'しゅ', she: 'しぇ', sho: 'しょ',
  cha: 'ちゃ', chu: 'ちゅ', che: 'ちぇ', cho: 'ちょ',
}
// 拗音 (kya, sya, tya, jya ...)。子音 + y + a/u/o
const YOON_HEAD: Record<string, string> = {
  k: 'き', s: 'し', t: 'ち', c: 'ち', n: 'に', h: 'ひ', m: 'み', r: 'り',
  g: 'ぎ', z: 'じ', j: 'じ', d: 'ぢ', b: 'び', p: 'ぴ',
}
const YOON_TAIL: Record<string, string> = { a: 'ゃ', u: 'ゅ', o: 'ょ' }
const VOWELS = 'aiueo'

/**
 * ローマ字 (ヘボン式・訓令式どちらでも) をひらがなに直す。直せない文字があれば null
 * (= ローマ字ではない。英単語など)。**打ちかけの末尾の子音** (「tenp」の p) は落とす —
 * 打っている途中でも、そこまでで探せるように。
 */
export function romajiToHiragana(input: string): string | null {
  const s = input.toLowerCase()
  if (!/^[a-z']+$/.test(s)) return null
  let out = ''
  let i = 0
  while (i < s.length) {
    const c = s[i]
    const next = s[i + 1]
    if (c === "'") { i += 1; continue }
    // ん: n' / nn / n の後が子音 (y 以外) / 末尾。m の後が b・p・m (tempura) も ん
    if (c === 'n' && next === "'") { out += 'ん'; i += 2; continue }
    // nn は、後ろが母音か y なら「ん + な行」(konnyaku = こんにゃく、annindofu = あんにん…)。
    // 2 つ目の n は次の音に使うので 1 文字だけ進める。それ以外 (末尾・子音の前) は nn で ん
    if (c === 'n' && next === 'n') {
      const after = s[i + 2]
      out += 'ん'
      i += after !== undefined && (VOWELS.includes(after) || after === 'y') ? 1 : 2
      continue
    }
    if (c === 'n' && (next === undefined || (!VOWELS.includes(next) && next !== 'y'))) {
      out += 'ん'; i += 1; continue
    }
    if (c === 'm' && next !== undefined && 'bpm'.includes(next)) { out += 'ん'; i += 1; continue }
    // 促音: 同じ子音が 2 つ (kk, tt, ss ...) と tch (matcha)
    if (!VOWELS.includes(c) && c === next && c !== 'n') { out += 'っ'; i += 1; continue }
    if (c === 't' && next === 'c' && s[i + 2] === 'h') { out += 'っ'; i += 1; continue }
    // 3 文字 → 2 文字 → 1 文字の順に表を引く
    const three = s.slice(i, i + 3)
    const two = s.slice(i, i + 2)
    if (BASE[three]) { out += BASE[three]; i += 3; continue }
    // 拗音: kya / sya / jya / chya は無いので cha 等は BASE で済む
    if (s[i + 1] === 'y' && YOON_HEAD[c] && s[i + 2] && YOON_TAIL[s[i + 2]]) {
      out += YOON_HEAD[c] + YOON_TAIL[s[i + 2]]; i += 3; continue
    }
    if (BASE[two]) { out += BASE[two]; i += 2; continue }
    if (BASE[c]) { out += BASE[c]; i += 1; continue }
    // 直せない。末尾の打ちかけ (子音だけ) なら落として終わる
    if (/^[a-z]+$/.test(s.slice(i)) && !/[aiueo]/.test(s.slice(i)) && s.length - i <= 3) break
    return null
  }
  return out || null
}

// ---- 長音を落とす (ローマ字の入力だけ) ------------------------------------

const O_ROW = 'おこそとのほもよろごぞどぼぽょぉ'
const U_ROW = 'うくすつぬふむゆるぐずづぶぷゅぅ'

/**
 * ローマ字は長音の書き方が揺れる (unaju / unajuu、gyudon / gyuudon、ko-ra / kora)。
 * 読みと入力の両方からそれを落として比べる。あわせて、ローマ字では区別しにくい
 * づ/ず・ぢ/じ・を/お を揃える (茶漬け = chazuke)。
 */
export function foldForRomaji(s: string): string {
  let out = ''
  for (const ch of s.replace(/ー/g, '').replace(/づ/g, 'ず').replace(/ぢ/g, 'じ').replace(/を/g, 'お')) {
    const prev = out[out.length - 1]
    if (prev && ((ch === 'う' && (O_ROW.includes(prev) || U_ROW.includes(prev))) || (ch === 'お' && O_ROW.includes(prev)))) {
      continue
    }
    out += ch
  }
  return out
}

// ---- 照らし合わせ ---------------------------------------------------------

export interface MenuSearchQuery {
  /** 揃えた入力 (かな・漢字・英数字) */
  text: string
  /** 入力がローマ字として読めたときのひらがな (長音を落とした形)。読めなければ null */
  romajiKana: string | null
}

export function parseMenuSearch(raw: string): MenuSearchQuery | null {
  const text = normalizeForSearch(raw)
  if (!text) return null
  // ローマ字として読むときは、区切りの ' を残す (ten'ya = てんや。normalizeForSearch は記号を
  // 落とすので別に作る)。iPhone のキーボードが入れる ’ も ' として扱う。空白・- (bi-ru) は落とす
  const latin = raw.normalize('NFKC').toLowerCase().replace(/[’‘`]/g, "'").replace(/[\s\-ー]/g, '')
  const kana = /^[a-z']+$/.test(latin) ? romajiToHiragana(latin) : null
  return { text, romajiKana: kana ? foldForRomaji(kana) : null }
}

export function matchesMenuSearch(
  item: Pick<MenuItem, 'name' | 'nameReading'>,
  q: MenuSearchQuery,
): boolean {
  const name = normalizeForSearch(item.name || '')
  const reading = normalizeForSearch(item.nameReading || '')
  if (name.includes(q.text) || (reading && reading.includes(q.text))) return true
  if (q.romajiKana && reading && foldForRomaji(reading).includes(q.romajiKana)) return true
  return false
}
